import { encodeFileChunk } from '@br/protocol';

export const CHUNK_SIZE = 256 * 1024;
/** Pause sending while the socket has more than this queued. */
export const MAX_BUFFERED_BYTES = 1024 * 1024;

export interface ChunkPlan {
  fileIndex: number;
  chunkIndex: number;
  offset: number;
  length: number;
}

/**
 * Splits each file into fixed-size chunks. An empty file still yields one zero-length chunk so the
 * node sees the file at all.
 */
export function planChunks(sizes: number[], chunkSize = CHUNK_SIZE): ChunkPlan[] {
  const out: ChunkPlan[] = [];
  sizes.forEach((size, fileIndex) => {
    if (size <= 0) {
      out.push({ fileIndex, chunkIndex: 0, offset: 0, length: 0 });
      return;
    }
    let chunkIndex = 0;
    for (let offset = 0; offset < size; offset += chunkSize) {
      out.push({ fileIndex, chunkIndex, offset, length: Math.min(chunkSize, size - offset) });
      chunkIndex++;
    }
  });
  return out;
}

export interface UploadTransport {
  sendBinary: (buf: Uint8Array) => void;
  bufferedAmount: () => number;
}

export interface UploadOptions {
  files: File[];
  chooserId: string;
  transport: UploadTransport;
  /** Polled between chunks; return true to abort */
  cancelled?: () => boolean;
  onProgress?: (sentBytes: number, totalBytes: number) => void;
  /** ms between backpressure polls (test hook) */
  pollMs?: number;
}

function sleep(ms: number) {
  return new Promise<void>((r) => setTimeout(r, ms));
}

/** `Blob.arrayBuffer()` with a FileReader fallback for older engines (and jsdom). */
function readBlob(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsArrayBuffer(blob);
  });
}

/**
 * Streams the selected files over the viewer socket as `kind=2` binary chunks. Resolves when every
 * chunk has been handed to the socket; the caller then sends `file.end`.
 */
export async function uploadFiles(opts: UploadOptions): Promise<{ sentBytes: number }> {
  const { files, chooserId, transport } = opts;
  const total = files.reduce((n, f) => n + f.size, 0);
  const plan = planChunks(files.map((f) => f.size));
  let sent = 0;
  for (const chunk of plan) {
    if (opts.cancelled?.()) throw new Error('cancelled');
    while (transport.bufferedAmount() > MAX_BUFFERED_BYTES) {
      if (opts.cancelled?.()) throw new Error('cancelled');
      await sleep(opts.pollMs ?? 50);
    }
    const file = files[chunk.fileIndex]!;
    const bytes = new Uint8Array(
      await readBlob(file.slice(chunk.offset, chunk.offset + chunk.length)),
    );
    transport.sendBinary(
      encodeFileChunk(
        { chooserId, fileIndex: chunk.fileIndex, chunkIndex: chunk.chunkIndex },
        bytes,
      ),
    );
    sent += bytes.byteLength;
    opts.onProgress?.(sent, total);
  }
  return { sentBytes: sent };
}
